import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

/* ── CONSTANTS ── */
const PRIMARY   = '#303c6c';   /* matches --primary-color */
const PRIMARY_DARK = '#1a2344'; /* matches --primary-dark */
const PRIMARY_LIGHT = '#4a579d'; /* matches --primary-light */
const SECONDARY = '#b4c5e4';   /* matches --secondary-color */
const SEC_LIGHT = '#dbf0ff';   /* matches --secondary-light */
const NAVY      = '#1A1A1A';   /* matches --text-primary */
const GRAY      = '#4A4A4A';   /* matches --text-secondary */
const LIGHT_BG  = '#f7fafd';   /* matches --bg-light */

const LANGUAGES = [
  { code: 'en',  label: 'English',    native: 'English',      flag: '🇮🇳' },
  { code: 'hi',  label: 'Hindi',      native: 'हिन्दी',         flag: '🇮🇳' },
  { code: 'as',  label: 'Assamese',   native: 'অসমীয়া',        flag: '🟠' },
  { code: 'bn',  label: 'Bengali',    native: 'বাংলা',          flag: '🟡' },
  { code: 'brx', label: 'Bodo',       native: 'बड़ो',            flag: '🟢' },
  { code: 'mni', label: 'Manipuri',   native: 'মৈতৈলোন্',        flag: '🔵' },
  { code: 'lus', label: 'Mizo',       native: 'Mizo ṭawng',   flag: '🟣' },
  { code: 'kha', label: 'Khasi',      native: 'Khasi',        flag: '🔴' },
  { code: 'ne',  label: 'Nepali',     native: 'नेपाली',         flag: '⚪' },
  { code: 'kok', label: 'Kokborok',   native: 'কোকবরক',         flag: '🟤' },
  { code: 'nag', label: 'Nagamese',   native: 'Nagamese',     flag: '🟠' },
];

/* ── ORIGINAL ENGLISH CONTENT ── */
const ORIGINAL_CONTENT = {
  eyebrow: 'Getting Started',
  heroTitle: 'Your wellness journey',
  heroTitleAccent: 'starts here.',
  heroSub: "UXAAH makes professional mental health support simple, private, and accessible. Here's exactly how it works — from first click to first session.",
  steps: [
    {
      subtitle: 'Get started in minutes',
      title: 'Create Your Account',
      description: 'Sign up with just your email. Your information is protected with end-to-end encryption and complete confidentiality.',
      detail: 'No credit card required to register',
    },
    {
      subtitle: 'Find your perfect match',
      title: 'Browse Therapists',
      description: 'Explore profiles of our licensed therapists — their specialties, approach, and availability. Choose someone who truly resonates with you.',
      detail: 'Filter by specialty, language & availability',
    },
    {
      subtitle: 'On your schedule',
      title: 'Book a Session',
      description: 'Pick a time that works for you — morning, evening, weekends. Sessions are flexible and can be rescheduled with advance notice.',
      detail: 'Instant booking confirmation',
    },
    {
      subtitle: 'Talk, heal, grow',
      title: 'Start Your Session',
      description: 'Connect with your therapist via secure video call or chat — from the comfort of your home. Every session is private and judgment-free.',
      detail: 'HD video & encrypted messaging',
    },
  ],
  reassurance: [
    { title: '100% Confidential', desc: 'Everything shared in your sessions stays private. We never share your data.' },
    { title: 'Licensed Professionals', desc: 'Every therapist is verified, licensed, and trained in evidence-based methods.' },
    { title: 'No Judgment, Ever', desc: 'A safe, compassionate space designed around your comfort and healing.' },
  ],
  ctaTitle: 'Ready to take the first step?',
  ctaSub: "Join thousands who've found clarity, relief, and growth through UXAAH.",
  ctaBtn: 'Create Your Free Account',
  ctaNote: 'Free to sign up · No commitment required',
};

const STEP_ICONS = [
  <svg key="1" width="36" height="36" viewBox="0 0 36 36" fill="none">
    <circle cx="18" cy="12" r="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M6 30c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>,
  <svg key="2" width="36" height="36" viewBox="0 0 36 36" fill="none">
    <circle cx="16" cy="16" r="9" stroke="currentColor" strokeWidth="2"/>
    <path d="M23 23l7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>,
  <svg key="3" width="36" height="36" viewBox="0 0 36 36" fill="none">
    <rect x="5" y="7" width="26" height="24" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M5 14h26" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 4v6M24 4v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M11 21h4M21 21h4M11 27h4M21 27h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>,
  <svg key="4" width="36" height="36" viewBox="0 0 36 36" fill="none">
    <rect x="3" y="8" width="22" height="16" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M25 14l8-5v18l-8-5V14z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
  </svg>,
];

/* ── TRANSLATE via Claude API ── */
async function translateContent(langLabel) {
  const inputObj = {
    eyebrow: ORIGINAL_CONTENT.eyebrow,
    heroTitle: ORIGINAL_CONTENT.heroTitle,
    heroTitleAccent: ORIGINAL_CONTENT.heroTitleAccent,
    heroSub: ORIGINAL_CONTENT.heroSub,
    step0_subtitle: ORIGINAL_CONTENT.steps[0].subtitle,
    step0_title: ORIGINAL_CONTENT.steps[0].title,
    step0_description: ORIGINAL_CONTENT.steps[0].description,
    step0_detail: ORIGINAL_CONTENT.steps[0].detail,
    step1_subtitle: ORIGINAL_CONTENT.steps[1].subtitle,
    step1_title: ORIGINAL_CONTENT.steps[1].title,
    step1_description: ORIGINAL_CONTENT.steps[1].description,
    step1_detail: ORIGINAL_CONTENT.steps[1].detail,
    step2_subtitle: ORIGINAL_CONTENT.steps[2].subtitle,
    step2_title: ORIGINAL_CONTENT.steps[2].title,
    step2_description: ORIGINAL_CONTENT.steps[2].description,
    step2_detail: ORIGINAL_CONTENT.steps[2].detail,
    step3_subtitle: ORIGINAL_CONTENT.steps[3].subtitle,
    step3_title: ORIGINAL_CONTENT.steps[3].title,
    step3_description: ORIGINAL_CONTENT.steps[3].description,
    step3_detail: ORIGINAL_CONTENT.steps[3].detail,
    r0_title: ORIGINAL_CONTENT.reassurance[0].title,
    r0_desc: ORIGINAL_CONTENT.reassurance[0].desc,
    r1_title: ORIGINAL_CONTENT.reassurance[1].title,
    r1_desc: ORIGINAL_CONTENT.reassurance[1].desc,
    r2_title: ORIGINAL_CONTENT.reassurance[2].title,
    r2_desc: ORIGINAL_CONTENT.reassurance[2].desc,
    ctaTitle: ORIGINAL_CONTENT.ctaTitle,
    ctaSub: ORIGINAL_CONTENT.ctaSub,
    ctaBtn: ORIGINAL_CONTENT.ctaBtn,
    ctaNote: ORIGINAL_CONTENT.ctaNote,
  };

  const prompt = `Translate ALL values in this JSON object into ${langLabel}. Return ONLY a valid JSON object with identical keys. No explanation, no markdown, no code fences.\n\n${JSON.stringify(inputObj)}`;

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1000,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  const data = await response.json();
  const text = (data.content || []).map(b => b.text || '').join('');
  const clean = text.replace(/```json|```/g, '').trim();
  const p = JSON.parse(clean);

  return {
    eyebrow: p.eyebrow,
    heroTitle: p.heroTitle,
    heroTitleAccent: p.heroTitleAccent,
    heroSub: p.heroSub,
    steps: [0, 1, 2, 3].map(i => ({
      subtitle: p[`step${i}_subtitle`],
      title: p[`step${i}_title`],
      description: p[`step${i}_description`],
      detail: p[`step${i}_detail`],
    })),
    reassurance: [
      { title: p.r0_title, desc: p.r0_desc },
      { title: p.r1_title, desc: p.r1_desc },
      { title: p.r2_title, desc: p.r2_desc },
    ],
    ctaTitle: p.ctaTitle,
    ctaSub: p.ctaSub,
    ctaBtn: p.ctaBtn,
    ctaNote: p.ctaNote,
  };
}

/* ── TRANSLATE BAR ── */
const TranslateBar = ({ onTranslate, currentLang, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGUAGES.find(l => l.code === currentLang) || LANGUAGES[0];

  return (
    <div style={tbStyles.bar}>
      <div style={tbStyles.inner} ref={ref}>
        <span style={tbStyles.label}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: 6, flexShrink: 0 }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke={PRIMARY} strokeWidth="1.8"/>
            <path d="M2 12h20M12 2c-2.76 3.33-4 7-4 10s1.24 6.67 4 10M12 2c2.76 3.33 4 7 4 10s-1.24 6.67-4 10" stroke={PRIMARY} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          Translate page
        </span>

        <button
          onClick={() => !loading && setOpen(o => !o)}
          style={{ ...tbStyles.trigger, ...(loading ? tbStyles.triggerLoading : {}) }}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {loading ? (
            <><span style={tbStyles.spinner} /><span style={{ marginLeft: 8 }}>Translating…</span></>
          ) : (
            <>
              <span style={tbStyles.flag}>{current.flag}</span>
              <span>{current.native}</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 8, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </>
          )}
        </button>

        {open && (
          <div style={tbStyles.dropdown} role="listbox">
            <div style={tbStyles.dropdownHeader}>Select Language</div>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                role="option"
                aria-selected={lang.code === currentLang}
                onClick={() => { onTranslate(lang.code, lang.label); setOpen(false); }}
                style={{ ...tbStyles.option, ...(lang.code === currentLang ? tbStyles.optionActive : {}) }}
              >
                <span style={tbStyles.flag}>{lang.flag}</span>
                <span>
                  <span style={tbStyles.optionNative}>{lang.native}</span>
                  <span style={tbStyles.optionEn}> · {lang.label}</span>
                </span>
                {lang.code === currentLang && (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                    <path d="M2 7l3.5 3.5L12 4" stroke={PRIMARY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const tbStyles = {
  bar: {
    position: 'sticky', top: 0, zIndex: 100,
    backgroundColor: 'rgba(255,255,255,0.96)',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid #e5e7eb',
    padding: '10px 24px',
    display: 'flex', justifyContent: 'flex-end',
  },
  inner: { position: 'relative', display: 'flex', alignItems: 'center', gap: 12 },
  label: { display: 'flex', alignItems: 'center', fontSize: '0.82rem', color: GRAY, fontWeight: 500 },
  trigger: {
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', borderRadius: 100,
    border: `1.5px solid ${PRIMARY}`,
    backgroundColor: SEC_LIGHT, color: PRIMARY,
    fontSize: '0.85rem', fontWeight: 600,
    cursor: 'pointer', outline: 'none',
    minWidth: 160, justifyContent: 'center',
    transition: 'background-color 0.2s',
  },
  triggerLoading: { opacity: 0.75, cursor: 'wait' },
  flag: { fontSize: '1rem' },
  spinner: {
    display: 'inline-block', width: 13, height: 13,
    border: `2px solid ${PRIMARY}40`, borderTop: `2px solid ${PRIMARY}`,
    borderRadius: '50%', animation: 'hiw_spin 0.7s linear infinite',
  },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
    width: 250, backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb', borderRadius: 14,
    boxShadow: '0 12px 40px rgba(0,0,0,0.13)',
    overflow: 'hidden', zIndex: 200,
    maxHeight: 380, overflowY: 'auto',
  },
  dropdownHeader: {
    padding: '10px 16px 8px',
    fontSize: '0.72rem', fontWeight: 700,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: GRAY, borderBottom: '1px solid #f3f4f6',
  },
  option: {
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', textAlign: 'left',
    padding: '10px 16px', border: 'none',
    backgroundColor: 'transparent', cursor: 'pointer',
    fontSize: '0.875rem', color: NAVY,
    transition: 'background-color 0.12s',
  },
  optionActive: { backgroundColor: SEC_LIGHT },
  optionNative: { fontWeight: 600 },
  optionEn: { color: GRAY, fontSize: '0.78rem' },
};

/* ── DIVIDER ── */
const SectionDivider = () => (
  <hr style={{
    border: 0,
    height: '2px',
    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0), ${SECONDARY}, rgba(0,0,0,0))`,
    margin: 0,
  }} />
);

/* ── STEP CARD INNER CONTENT ── */
/* Extracted so we can reuse it inside both the plain div and the Link wrapper */
const StepCardContent = ({ step, index, isFirstStep }) => (
  <div
    style={{
      ...styles.stepCardInner,
      /* Step 1 gets a highlighted border to hint it's clickable */
      ...(isFirstStep ? {
        border: `1.5px solid ${PRIMARY}`,
        boxShadow: `0 4px 20px ${PRIMARY}18`,
      } : {}),
    }}
  >
    <div style={styles.stepIconWrap}>{STEP_ICONS[index]}</div>
    <div style={{ flex: 1 }}>
      <p style={styles.stepSubtitle}>{step.subtitle}</p>
      <h3 style={styles.stepTitle}>{step.title}</h3>
      <p style={styles.stepDesc}>{step.description}</p>
      <div style={styles.stepDetail}>
        <span style={styles.stepDetailDot} />
        {step.detail}
      </div>
      {/* Small "Sign up →" hint only on Step 1 */}
      {isFirstStep && (
        <div style={styles.stepCta}>
          Sign up free
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginLeft: 6 }}>
            <path d="M2 7h10M8 3l4 4-4 4" stroke={PRIMARY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
    </div>
  </div>
);

/* ── MAIN COMPONENT ── */
const HowItWorks = () => {
  const [visible, setVisible] = useState([]);
  const [currentLang, setCurrentLang] = useState('en');
  const [content, setContent] = useState(ORIGINAL_CONTENT);
  const [translating, setTranslating] = useState(false);
  const [cache, setCache] = useState({ en: ORIGINAL_CONTENT });
  const stepRefs = useRef([]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `@keyframes hiw_spin { to { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch(_) {} };
  }, []);

  useEffect(() => {
    const observers = stepRefs.current.map((ref, i) => {
      if (!ref) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setVisible(prev => [...new Set([...prev, i])]); },
        { threshold: 0.2 }
      );
      obs.observe(ref);
      return obs;
    });
    return () => observers.forEach(o => o && o.disconnect());
  }, []);

  const handleTranslate = useCallback(async (langCode, langLabel) => {
    if (langCode === currentLang) return;
    if (cache[langCode]) {
      setContent(cache[langCode]);
      setCurrentLang(langCode);
      return;
    }
    if (langCode === 'en') {
      setContent(ORIGINAL_CONTENT);
      setCurrentLang('en');
      return;
    }
    setTranslating(true);
    try {
      const translated = await translateContent(langLabel);
      setCache(prev => ({ ...prev, [langCode]: translated }));
      setContent(translated);
      setCurrentLang(langCode);
    } catch (err) {
      console.error('Translation failed:', err);
      alert('Translation failed. Please try again.');
    } finally {
      setTranslating(false);
    }
  }, [currentLang, cache]);

  return (
    <div style={styles.page}>

      {/* ── TRANSLATE BAR ── */}
      <TranslateBar onTranslate={handleTranslate} currentLang={currentLang} loading={translating} />

      {/* ── HERO ── */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <span style={styles.eyebrow}>{content.eyebrow}</span>
          <h1 style={styles.heroTitle}>
            {content.heroTitle}<br />
            <em style={styles.heroAccent}>{content.heroTitleAccent}</em>
          </h1>
          <p style={styles.heroSub}>{content.heroSub}</p>
        </div>
        <div style={styles.heroDecor} aria-hidden="true">
          <div style={styles.decorCircle1} />
          <div style={styles.decorCircle2} />
        </div>
      </section>

      {/* ── DIVIDER ── */}
      <SectionDivider />

      {/* ── STEPS ── */}
      <section style={styles.stepsSection}>
        <div style={styles.stepsContainer}>
          <div style={styles.stepsTimeline}>
            {content.steps.map((step, i) => {
              const isVisible = visible.includes(i);
              const isEven = i % 2 === 0;
              const isFirstStep = i === 0;

              return (
                <div
                  key={i}
                  ref={el => (stepRefs.current[i] = el)}
                  style={{
                    ...styles.stepRow,
                    flexDirection: isEven ? 'row' : 'row-reverse',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                    transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`,
                  }}
                >
                  {/* ── Step card — Step 1 is wrapped in a Link ── */}
                  <div style={styles.stepCard}>
                    {isFirstStep ? (
                      <Link
                        to="/signup"
                        style={{ textDecoration: 'none', display: 'block' }}
                        aria-label="Create your account — go to sign up"
                      >
                        <StepCardContent step={step} index={i} isFirstStep={true} />
                      </Link>
                    ) : (
                      <StepCardContent step={step} index={i} isFirstStep={false} />
                    )}
                  </div>

                  {/* ── Timeline node ── */}
                  <div style={styles.stepNode}>
                    <div style={styles.stepNodeInner}>
                      <span style={styles.stepNumber}>0{i + 1}</span>
                    </div>
                  </div>

                  <div style={styles.stepSpacer} />
                </div>
              );
            })}
            <div style={styles.timelineLine} aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ── DIVIDER ── */}
      <SectionDivider />

      {/* ── REASSURANCE ── */}
      <section style={styles.reassurance}>
        <div style={styles.reassuranceGrid}>
          {content.reassurance.map((r, i) => (
            <div key={i} style={styles.reassuranceCard}>
              <span style={styles.reassuranceIcon}>{['🔒', '🎓', '💬'][i]}</span>
              <h4 style={styles.reassuranceTitle}>{r.title}</h4>
              <p style={styles.reassuranceDesc}>{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={styles.cta}>
        <div style={styles.ctaInner}>
          <h2 style={styles.ctaTitle}>{content.ctaTitle}</h2>
          <p style={styles.ctaSub}>{content.ctaSub}</p>
          <Link to="/signup" style={styles.ctaBtn}>{content.ctaBtn}</Link>
          <p style={styles.ctaNote}>{content.ctaNote}</p>
        </div>
      </section>

    </div>
  );
};

/* ── PAGE STYLES ── */
const styles = {
  page: {
    fontFamily: "Cambria, Cochin, Georgia, Times, 'Times New Roman', serif",
    backgroundColor: '#ffffff',
    color: NAVY,
    overflowX: 'hidden',
  },

  /* Hero */
  hero: {
    position: 'relative',
    padding: '100px 24px 80px',
    textAlign: 'center',
    background: `linear-gradient(160deg, ${SEC_LIGHT} 0%, #ffffff 60%)`,
    overflow: 'hidden',
  },
  heroInner: { position: 'relative', zIndex: 1, maxWidth: 680, margin: '0 auto' },
  eyebrow: {
    display: 'inline-block',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: PRIMARY,
    backgroundColor: SEC_LIGHT,
    padding: '6px 14px',
    borderRadius: 100,
    marginBottom: 24,
    border: `1px solid ${SECONDARY}`,
  },
  heroTitle: {
    fontSize: 'clamp(2.2rem, 5vw, 3.6rem)',
    fontWeight: 700,
    lineHeight: 1.15,
    margin: '0 0 20px',
    color: PRIMARY,
  },
  heroAccent: { fontStyle: 'italic', color: PRIMARY_LIGHT },
  heroSub: {
    fontSize: 'clamp(1rem, 2vw, 1.125rem)',
    color: GRAY,
    lineHeight: 1.7,
    maxWidth: 520,
    margin: '0 auto',
  },
  heroDecor: { position: 'absolute', inset: 0, pointerEvents: 'none' },
  decorCircle1: {
    position: 'absolute', top: -80, right: -80,
    width: 320, height: 320, borderRadius: '50%',
    background: `radial-gradient(circle, ${PRIMARY}18 0%, transparent 70%)`,
  },
  decorCircle2: {
    position: 'absolute', bottom: -60, left: -60,
    width: 240, height: 240, borderRadius: '50%',
    background: `radial-gradient(circle, ${PRIMARY}10 0%, transparent 70%)`,
  },

  /* Steps */
  stepsSection: { padding: '96px 24px', backgroundColor: '#ffffff' },
  stepsContainer: { maxWidth: 900, margin: '0 auto' },
  stepsTimeline: { position: 'relative', display: 'flex', flexDirection: 'column', gap: 40 },
  timelineLine: {
    position: 'absolute', left: '50%', top: 32, bottom: 32,
    width: 2,
    backgroundColor: `${SECONDARY}88`,
    transform: 'translateX(-50%)',
    zIndex: 0,
  },
  stepRow: { display: 'flex', alignItems: 'center', gap: 0, position: 'relative', zIndex: 1 },
  stepCard: { flex: 1, padding: '0 24px' },
  stepCardInner: {
    backgroundColor: LIGHT_BG,
    border: `1px solid ${SECONDARY}`,
    borderRadius: 16,
    padding: '28px 28px',
    display: 'flex',
    gap: 20,
    alignItems: 'flex-start',
    transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
  },
  stepIconWrap: {
    flexShrink: 0,
    width: 56, height: 56,
    backgroundColor: SEC_LIGHT,
    borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: PRIMARY,
  },
  stepSubtitle: {
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    color: PRIMARY_LIGHT, margin: '0 0 4px',
  },
  stepTitle: { fontSize: '1.15rem', fontWeight: 700, color: PRIMARY, margin: '0 0 10px' },
  stepDesc: { fontSize: '0.9rem', color: GRAY, lineHeight: 1.65, margin: '0 0 14px' },
  stepDetail: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: '0.8rem', color: PRIMARY_LIGHT, fontWeight: 500,
  },
  stepDetailDot: {
    width: 6, height: 6, borderRadius: '50%',
    backgroundColor: PRIMARY_LIGHT, flexShrink: 0,
  },

  /* "Sign up free →" micro-CTA shown only on Step 1 */
  stepCta: {
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: 14,
    fontSize: '0.82rem',
    fontWeight: 700,
    color: PRIMARY,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: `1.5px solid ${PRIMARY}`,
    paddingBottom: 1,
  },

  stepNode: {
    flexShrink: 0, width: 64, height: 64,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2,
  },
  stepNodeInner: {
    width: 56, height: 56, borderRadius: '50%',
    backgroundColor: PRIMARY,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: `0 0 0 6px ${SEC_LIGHT}, 0 0 0 8px ${SECONDARY}`,
  },
  stepNumber: { color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.05em' },
  stepSpacer: { flex: 1 },

  /* Reassurance */
  reassurance: { backgroundColor: LIGHT_BG, padding: '80px 24px' },
  reassuranceGrid: {
    maxWidth: 900, margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 24,
  },
  reassuranceCard: {
    backgroundColor: '#ffffff',
    border: `1px solid ${SECONDARY}`,
    borderRadius: 16,
    padding: '32px 28px',
    textAlign: 'center',
  },
  reassuranceIcon: { fontSize: '2rem', display: 'block', marginBottom: 16 },
  reassuranceTitle: { fontSize: '1.05rem', fontWeight: 700, color: PRIMARY, margin: '0 0 8px' },
  reassuranceDesc: { fontSize: '0.875rem', color: GRAY, lineHeight: 1.65, margin: 0 },

  /* CTA */
  cta: {
    padding: '100px 24px',
    textAlign: 'center',
    background: `linear-gradient(160deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
  },
  ctaInner: { maxWidth: 540, margin: '0 auto' },
  ctaTitle: {
    fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
    fontWeight: 700,
    color: '#ffffff',
    margin: '0 0 16px',
  },
  ctaSub: { fontSize: '1rem', color: 'rgba(255,255,255,0.70)', lineHeight: 1.7, marginBottom: 36 },
  ctaBtn: {
    display: 'inline-block',
    backgroundColor: 'transparent',
    color: '#ffffff',
    textDecoration: 'none',
    padding: '16px 40px',
    borderRadius: 35,
    fontWeight: 700,
    fontSize: '1rem',
    letterSpacing: '0.02em',
    border: '2px solid #ffffff',
    transition: 'background-color 0.3s ease',
  },
  ctaNote: { fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: 16 },
};

export default HowItWorks;